import React, { useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { validateFiles, formatFileSize } from "../utils/fileValidation";
import { toast } from "sonner";

interface FilePickerZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  inputId: string;
  label?: string;
}

export const FilePickerZone: React.FC<FilePickerZoneProps> = ({
  files,
  onChange,
  disabled = false,
  inputId,
  label = "Attachments (Optional)",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    const { validFiles, invalidFiles } = validateFiles(selected);

    if (invalidFiles.length > 0) {
      toast.error(invalidFiles.map(({ error }: { error: string }) => error).join(", "));
    }

    if (validFiles.length > 0) {
      onChange([...files, ...validFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium leading-none">{label}</p>
      <div className="border-2 border-dashed border-border rounded-lg p-4">
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,video/mp4,video/quicktime,video/webm,video/x-msvideo,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,.jpg,.jpeg,.png,.webp,.heic,.mp4,.mov,.webm,.avi,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
        <label
          htmlFor={inputId}
          className={`cursor-pointer flex flex-col items-center justify-center gap-2 py-2 ${
            disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
          }`}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Click to select files or drag and drop
          </span>
          <span className="text-xs text-muted-foreground">
            Supported: Images, Videos, Documents (max 100MB per file)
          </span>
        </label>

        {files.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Files will upload in background after saving
            </p>
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-muted rounded-md"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <ImageIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(index)}
                  className="h-8 w-8 p-0"
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
